import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ErrorResponse } from '@sparkai/utils'

import { apiClient } from '../apiClient'
import { CONFIG_ALERTS_QUERY_KEY } from './config-alerts/useConfigAlerts'

type ProgramOrganizationLogo = {
  id: string
  location: string | null
  thumbnailSmLocation: string | null
  thumbnailLgLocation: string | null
}

type ProgramOrganization = {
  id: string
  name: string
  logoMediaId?: string | null
  logo?: ProgramOrganizationLogo | null
}

type ProgramGroup = {
  id: string
  name: string
}

export type Program = {
  id: string
  name: string
  annotationType: string
  subrequestIds: string[]
  minimumResolutions: number
  ownerId: string
  customerEmail: string
  operatorDescription: string
  sopVersion: string | null
  autoAuditEnabled: boolean
  active: boolean
  engagementLifetime: number
  color: string
  displayName: string
  operatorDisplayName: string
  instructions: string
  labelSet: string[]
  responseSet: string[]
  iconId: string
  engagementLevelAnnotations: boolean
  extendedFeedback: boolean
  operatorRoleRequirement: boolean
  developmentMode: boolean
  auditConsensus: boolean
  iconType: string
  internalNotes: string | null
  slaResponseTime: number | null
  targetAccuracy: number
  lowPriorityQueueTimeout: number | null
  taskInactivityTime: number | null
  apiVersion: number
  hourlyLimit: number | null
  dailyLimit: number | null
  monthlyLimit: number | null
  perClientIdLimit: number | null
  concurrentLimit: number | null
  mouseMoveTimeout: number | null
  autoEligibility: boolean
  denormalizationEnabled: boolean
  availabilityTimezone: string | null
  isRecycler: boolean
  isSuperMajority: boolean
  organization: ProgramOrganization | null
  programGroup: ProgramGroup | null
}

type ProgramListOwner = {
  name: string | null
  email: string
}

type ProgramListImage = {
  id: string
  location: string | null
}

export type ProgramListItem = Program & {
  dateCreated: string | null
  dateUpdated: string | null
  operatorSummary: string
  priority: number
  eligibilityScoreThreshold: number
  eligibilitySampleThreshold: number
  auditLifetime: number | null
  maximumResolutions: number
  metricSet: string[]
  customerDescription: string
  customerFlowRate: number
  customerTargetEngagements: number
  customerTargetResponseTime: number
  targetSpeed: number
  cooldownInSeconds: number | null
  labelCountLimit: number | null
  concurrentEngagementsLimit: boolean | null
  beqTimeframeInMinutes: number | null
  beqPerBlockLimit: number | null
  engagementApiEnabled: boolean
  targetResolutionAccuracy: number | null
  owner: ProgramListOwner | null
  image: ProgramListImage | null
  icon: ProgramListImage | null
  subprogram: string | null
}

type ProgramStatus = 'active' | 'inactive'

type ProgramBasicInformation = {
  id: string
  name: string
  operatorDisplayName: string
  description: string
  code: string
  status: ProgramStatus
  icon: string
  color: string
  organization: {
    id: string
    name: string
    avatarUrl: string
  }
  sop_version: string
  api_version: number
  development_mode: boolean
  auto_audit_enabled: boolean
}

export type ProgramWithBasicInformation = Program & {
  basicInformation: ProgramBasicInformation
}

type PlatformProgramResponseListResponse = {
  programId: string
  startDate?: Date
  endDate?: Date
  responses: string[]
}

type PlatformProgramV2ListRequest = {
  name?: string
  displayName?: string
  customerEmail?: string
  instructions?: string
  apiVersion?: number
  operatorRoleRequirement?: boolean
  orgIds?: string[]
  active?: boolean
  startCreatedDate?: string
  endCreatedDate?: string
  startPriority?: number
  endPriority?: number
  startMaximumResolutions?: number
  endMaximumResolutions?: number
  startEngagementLifetime?: number
  endEngagementLifetime?: number
  startEligibilityScoreThreshold?: number
  endEligibilityScoreThreshold?: number
  startEligibilitySampleThreshold?: number
  endEligibilitySampleThreshold?: number
  limit?: number
  orderBy?: 'name' | 'displayName' | 'dateCreated'
  sortDescending?: boolean
}

export type UpdateProgramRequest = {
  programId: string
  displayName?: string
  operatorDisplayName?: string
  iconType?: string
  color?: string
  organizationId?: string
  customerEmail?: string
  active?: boolean
  operatorDescription?: string
  sopVersion?: string | null
  apiVersion?: number
  developmentMode?: boolean
  autoAuditEnabled?: boolean
  slaResponseTime?: number | null
  targetAccuracy?: number
  engagementLifetime?: number
  lowPriorityQueueTimeout?: number | null
  taskInactivityTime?: number | null
  mouseMoveTimeout?: number | null
  auditConsensus?: boolean
}

type CreateProgramRequest = {
  displayName: string
  description: string
  internalNotes?: string
  orgId: string
  name: string
  color: string
  icon: string
  customerEmail: string
}

type UpdateProgramBasicInformationRequest = {
  programId: string
  name: string
  operatorDisplayName: string
  icon: string
  color: string
  organizationId: string
  customerEmail: string
  active: boolean
  description: string
  sopVersion?: string
  developmentMode: boolean
  autoAuditEnabled: boolean
}

type UpdateProgramBasicInformationResponse = Omit<UpdateProgramBasicInformationRequest, 'programId'> & {
  id: string
}

type UpdateProgramResolutionTargetsRequest = {
  programId: string
  slaResponseTime: number
  targetAccuracy: number
  engagementLifetime: number
  lowPriorityQueueTimeout: number
  taskInactivityTime: number
  mouseMoveTimeout: number
}

type UpdateProgramResolutionTargetsResponse = Omit<UpdateProgramResolutionTargetsRequest, 'programId'> & {
  id: string
}

type ResolutionAnnotationClass = {
  label: string
}

export type ProgramInstanceCatalogProgram = {
  id: string
  name: string | null
  displayName: string | null
  targetAccuracy: number | null
}

export type ProgramInstanceCatalogItem = {
  // The backend derives Program Instances from Live programs; `id` matches `liveProgram.id`.
  id: string
  name: string | null
  displayName: string | null
  programGroup: {
    id: string
    name: string
    msDisplayName: string | null
  }
  iconType: string | null
  color: string | null
  liveProgram: ProgramInstanceCatalogProgram
  recyclerProgram: ProgramInstanceCatalogProgram | null
  superMajorityProgram?: ProgramInstanceCatalogProgram | null
}

type ProgramInstanceCatalogResponse = {
  instances: ProgramInstanceCatalogItem[]
}

export const PROGRAM_BY_ID_KEY = 'program-by-id'
export const PROGRAM_INSTANCES_CATALOG_KEY = 'program-instances-catalog'

export function useGetById(programId?: string) {
  return useQuery({
    queryKey: [PROGRAM_BY_ID_KEY, programId],
    queryFn: async () => (await apiClient.get<Program>(`/int/v3/programs/${programId}`)).data,
    enabled: Boolean(programId),
    refetchOnWindowFocus: false,
    select: (data: Program): ProgramWithBasicInformation => ({
      ...data,
      basicInformation: {
        id: data.id,
        name: data.displayName,
        operatorDisplayName: data.operatorDisplayName,
        code: data.name,
        description: data.operatorDescription,
        status: data.active ? 'active' : 'inactive',
        color: data.color,
        icon: data.iconType,
        organization: {
          id: data.organization?.id ?? '',
          name: data.organization?.name ?? '',
          avatarUrl: data.organization?.logo?.location ?? '',
        },
        sop_version: data.sopVersion ?? '',
        api_version: data.apiVersion,
        development_mode: data.developmentMode,
        auto_audit_enabled: data.autoAuditEnabled,
      },
    }),
  })
}

export function useEdit(onSuccess?: () => void, onError?: () => void) {
  const queryClient = useQueryClient()

  return useMutation<Program, ErrorResponse, UpdateProgramRequest>({
    mutationFn: async ({ programId, ...payload }) => {
      const { data } = await apiClient.put<Program>(`/int/v3/programs/${programId}`, payload)
      return data
    },
    onSuccess: (data: Program) => {
      queryClient.invalidateQueries({
        queryKey: [PROGRAM_BY_ID_KEY, data.id],
      })
      queryClient.invalidateQueries({ queryKey: ['platform-program-list'] })
      queryClient.invalidateQueries({ queryKey: ['platform-program-v2-list'] })
      queryClient.invalidateQueries({ queryKey: ['active-programs'] })
      onSuccess?.()
    },
    onError,
  })
}

export function useUpdateBasicInformation() {
  const queryClient = useQueryClient()

  return useMutation<
    UpdateProgramBasicInformationResponse,
    ErrorResponse,
    UpdateProgramBasicInformationRequest
  >({
    mutationFn: async ({ programId, ...payload }) => {
      const { data } = await apiClient.put<UpdateProgramBasicInformationResponse>(
        `/int/v3/programs/${programId}/config/basic-information`,
        payload
      )
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PROGRAM_BY_ID_KEY, variables.programId],
      })
      queryClient.invalidateQueries({ queryKey: ['platform-program-list'] })
      queryClient.invalidateQueries({ queryKey: ['platform-program-v2-list'] })
      queryClient.invalidateQueries({ queryKey: ['active-programs'] })
    },
  })
}

export function useUpdateResolutionTargets() {
  const queryClient = useQueryClient()

  return useMutation<
    UpdateProgramResolutionTargetsResponse,
    ErrorResponse,
    UpdateProgramResolutionTargetsRequest
  >({
    mutationFn: async ({ programId, ...payload }) => {
      const { data } = await apiClient.put<UpdateProgramResolutionTargetsResponse>(
        `/int/v3/programs/${programId}/config/resolution-targets`,
        payload
      )
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PROGRAM_BY_ID_KEY, variables.programId],
      })
      queryClient.invalidateQueries({ queryKey: ['platform-program-list'] })
      queryClient.invalidateQueries({ queryKey: ['platform-program-v2-list'] })
      queryClient.invalidateQueries({ queryKey: ['active-programs'] })
      queryClient.invalidateQueries({ queryKey: [CONFIG_ALERTS_QUERY_KEY, variables.programId] })
    },
  })
}

export function useGetList() {
  return useQuery<ProgramListItem[]>({
    queryKey: ['platform-program-list'],
    queryFn: async () => {
      const { data } = await apiClient.get<ProgramListItem[]>('/int/v3/programs')
      return data
    },
  })
}

export function useGetResponses(programId?: string) {
  return useQuery<PlatformProgramResponseListResponse>({
    queryKey: ['platform-program-responses', programId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/int/v3/programs/${programId}/responses`)
      return data
    },
    enabled: Boolean(programId),
    refetchOnWindowFocus: false,
  })
}

export function useGetV2List(params: PlatformProgramV2ListRequest) {
  return useInfiniteQuery<ProgramListItem[]>({
    queryKey: ['platform-program-v2-list', params],
    queryFn: async ({ pageParam = 0 }) => {
      const { data } = await apiClient.get<ProgramListItem[]>('/int/v3/programs', {
        params: { ...params, cursor: `${pageParam}` },
      })
      return data
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages: ProgramListItem[][]) =>
      lastPage.length < (params.limit ?? 10) ? undefined : allPages.flat().length,
    refetchOnWindowFocus: false,
  })
}

export function useGetActivePrograms() {
  return useQuery({
    queryKey: ['active-programs'],
    queryFn: async () => {
      const response = await apiClient.get<ProgramListItem[]>('/int/v3/programs', {
        params: { active: true, limit: 100 },
      })
      return response.data ?? []
    },
  })
}

export function useGetProgramInstancesCatalog(enabled = true) {
  return useQuery({
    queryKey: [PROGRAM_INSTANCES_CATALOG_KEY],
    queryFn: async () => {
      const { data } = await apiClient.get<ProgramInstanceCatalogResponse>('/int/v3/program-groups/instances')
      return data
    },
    enabled,
    refetchOnWindowFocus: false,
  })
}

export function useCreate() {
  return useMutation({
    mutationFn: async (values: CreateProgramRequest) => {
      const { data } = await apiClient.post<{ id: string }>(`int/v3/programs`, values)
      return data
    },
  })
}

export function useAutoEligibilityWork() {
  return useMutation({
    mutationFn: async ({ programId, enabled }: { programId: string; enabled: boolean }) => {
      const { data } = await apiClient.put<{ id: string }>(`int/v3/programs/${programId}/auto-eligibility`, {
        autoEligibility: enabled,
      })
      return data
    },
  })
}

export function useReminder() {
  return useMutation({
    mutationFn: async ({ programId, operatorIds }: { programId: string; operatorIds: string[] }) => {
      const { data } = await apiClient.post(`/int/v3/programs/${programId}/training-reminder`, {
        operatorIds,
      })
      return data
    },
  })
}

export const useGetResolutionAnnotationClasses = (programId?: string) => {
  return useQuery<string[], ErrorResponse>({
    queryKey: ['program', programId, 'resolution-annotation-classes'],
    queryFn: async () => {
      const { data } = await apiClient.get<{
        classes: ResolutionAnnotationClass[]
      }>(`/int/v3/programs/${programId}/resolution-annotation-classes`)
      return data.classes.map((cls) => cls.label)
    },
    enabled: Boolean(programId),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  })
}
